<template>
    <div>
        <template v-if="loading">
            <loading-spinner/>
        </template>
        <template v-if="!loading">
            <div>
                <data-list v-model="summaryData"/>
            </div>
        </template>
    </div>
</template>

<script>

import DataList from '~/components/Shared/UIComponents/DataList';
import productConfig from '~/mixins/productConfig';

export default {
    name: 'summaryData',
    components: {
        DataList
    },
    mixins: [productConfig,],
    props: {
        value: Object,
        isReinsurer: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: false,
            skipProductProperties: [],
        }
    },
    computed: {
        risk() {
            return this.value;
        },
        summaryData() {
            const summaryDataList = this.genericData;

            if (this.isCommitment && this.isDeclaration) {
                summaryDataList.push({
                    text: 'Available capacity',
                    value: this.formatCurrency(this.value.available_capacity)
                })
            }

            //risk period fields where type = risk_period
            summaryDataList.push(...this.riskPeriodFields);

            //start date, end date and total risk period
            summaryDataList.push(...this.datesAndTotalRiskPeriodData);

            //captive insurance specific data
            summaryDataList.push(...this.captiveInsuranceData);

            if (this.isDeclaration) {
                if (this.value.umr) {
                    summaryDataList.push(
                        {
                            text: 'Declaration UMR',
                            value: this.value.umr
                        }
                    )
                }
                if (this.value.reference && !this.isAcceptedDeclaration) {
                    summaryDataList.push(
                        {
                            text: 'Policy reference',
                            value: this.value.reference
                        }
                    )
                }
            } else if (this.isFirmOrder && this.firmOrder.policy_reference) {
                summaryDataList.push(
                    {
                        text: 'Policy reference',
                        value: this.firmOrder.policy_reference
                    }
                )
            }

            if (this.value.deductible) {
                summaryDataList.push(
                    {
                        text: 'Deductible',
                        value: this.value.deductible
                    }
                );
            }

            if (this.productHasProperty('gross_margin') && this.value.gross_margin) {
                summaryDataList.push(
                    {
                        text: this.getPropertyLabel('gross_margin'),
                        value: this.$options.filters.numberNoTrail(this.value.gross_margin) + ' bps'
                    },
                );
            }

            if (this.productHasProperty('net_margin') && this.value.net_margin) {
                summaryDataList.push(
                    {
                        text: this.getPropertyLabel('net_margin'),
                        value: this.$options.filters.numberNoTrail(this.value.net_margin) + ' bps'
                    },
                );
            }

            if (this.isDeclaration) {
                summaryDataList.push(
                    {
                        text: this.getPropertyLabel('waiting_period'),
                        value: `${this.value.waiting_period} days`
                    }
                );
            } else if (this.value.hasOwnProperty('requested_waiting_period') && this.value.requested_waiting_period !== null) {
                summaryDataList.push(
                    {
                        text: this.getPropertyLabel('requested_waiting_period'),
                        value: `${this.value.requested_waiting_period} days`
                    }
                );
            }

            if (this.value.sub_sector_id !== null) {
                summaryDataList.push(
                    {
                        text: 'Sector',
                        value: this.value.sub_sector.sector.name
                    },
                    {
                        text: 'Sub sector',
                        value: this.value.custom_sub_sector ? this.value.custom_sub_sector : this.value.sub_sector.name
                    }
                );
            }

            //product specific data
            summaryDataList.push(...this.productDataFields);

            //brokers data
            summaryDataList.push(...this.brokersData);

            // placing broker contact data
            if (this.value.placing_broker_contact) {
                summaryDataList.push(...this.placingBrokerContactData);
            }

            if (this.isDeclaration) {
                summaryDataList.push(...this.declarationData);
            }

            return summaryDataList;
        },
        isAcceptedDeclaration() {
            return this.value.risk_structure === 'accepted_declaration';
        },
        firmOrder() {
            return this.value.firm_orders.find(fo => ['captive_reviewing', 'reviewing', 'bound'].includes(fo.status));
        },
        isCaptiveReinsurance() {
            return this.value.transaction_type === 'captive_reinsurance';
        },
        isFirmOrder() {
            return this.value.risk_type === 'submission' && this.value.firm_orders && this.value.firm_orders.filter(fo => ['captive_reviewing', 'reviewing', 'bound'].includes(fo.status)).length > 0;
        },
        isDeclaration() {
            return this.value.risk_type === 'declaration';
        },
        isOffRisk() {
            return this.value.status === "off_risk" || this.hasApprovedOffRiskEndorsement;
        },
        isCommitment() {
            return this.value.quote_type.key === 'commitment';
        },
        isInvoice() {
            return this.value.quote_type.key === 'invoice';
        },
        genericData() {
            const genericData = [];
            if (this.isDeclaration && this.value.classic_declaration_id) {
                genericData.push({
                        text: 'Tepfin classic quote id',
                        value: this.value.submission ? this.value.submission.classic_quote_id : this.value.classic_quote_id
                    },
                    {
                        text: 'Tepfin classic declaration id',
                        value: this.value.classic_declaration_id
                    }
                );
            }

            genericData.push({
                text: 'Quote type',
                value: this.value.quote_type.name
            });

            if (this.isInvoice) {
                genericData.push({
                    text: 'Commitment id',
                    value: this.value.commitment_declaration.risk_id
                })
            }

            genericData.push(
                {
                    text: 'Client',
                    value: this.isReinsurer ? this.value.facility.captive_insurer_company.name : this.value.client_company.name
                },
                {
                    text: 'Client reference',
                    value: this.value.client_reference
                },
                {
                    text: 'Facility name',
                    value: this.value.facility && this.value.facility.name ? this.value.facility.name : 'No facility'
                },
                {
                    text: 'Facility UMR',
                    value: this.value.facility && this.value.facility.UMR ? this.value.facility.UMR : 'No facility'
                },
            );

            if (this.value.entity?.name) {
                genericData.push(
                    {
                        text: 'Entity',
                        value: this.value.entity?.name
                    },
                );
            }

            if (this.value.client_code_type && this.value.client_code) {

                genericData.push(
                    {
                        text: 'Placing broker entity',
                        value: this.value.client_code_type.toUpperCase()
                    },
                );

                if (!this.isAcceptedDeclaration) {
                    genericData.push(
                        {
                            text: 'Client code',
                            value: this.value.client_code
                        },
                    );
                }
            }

            if (this.value.country_id) {
                genericData.push(
                    {
                        text: 'Country of origin',
                        value: this.value.country.name,
                    }
                )
            }

            genericData.push(
                {
                    text: 'Category',
                    value: this.value.category.name
                },
                {
                    text: 'Product',
                    value: this.value.product.name
                },
            );

            // this check is made as already existing risks would not have these fields for Accepted Declaration
            if (
                this.value.choice_of_law &&
                this.value.interest &&
                this.value.taxes_payable_by_insured &&
                this.value.taxes_payable_by_insurer &&
                this.value.regulatory_risk_location &&
                this.value.us_classification &&
                this.value.regulatory_client_classification &&
                this.value.risk_codes &&
                this.value.taxes_payable_by_insurer_and_administered_by_the_insured &&
                this.value.taxes_payable_and_administered_by_the_insured
            ) {
                genericData.push(
                    {
                        text: 'Choice of law and Jurisdiction',
                        value: this.value?.choice_of_law?.name || ''
                    },
                    {
                        text: 'Interest',
                        value: this.value.interest
                    },
                    {
                        text: 'Taxes payable by Insured & administered by Insurer(s)',
                        value: this.value.taxes_payable_by_insured + '%'
                    },
                    {
                        text: 'Taxes payable and administered by Insurer(s)',
                        value: this.value.taxes_payable_by_insurer + '%'
                    },
                    {
                        text: 'Taxes payable by Insurer(s) and administered by the Insured or their agent',
                        value: this.value.taxes_payable_by_insurer_and_administered_by_the_insured + '%'
                    },
                    {
                        text: 'Taxes payable and administered by the Insured or their agent',
                        value: this.value.taxes_payable_and_administered_by_the_insured + '%'
                    },
                    {
                        text: 'Regulatory risk location',
                        value: this.value.regulatory_risk_location?.name
                    },
                    {
                        text: 'US Classification',
                        value: this.value.us_classification
                    },
                    {
                        text: 'Regulatory Client classification',
                        value: this.value.regulatory_client_classification
                    },
                    {
                        text: 'Allocation of premium to coding',
                        value: this.allocationOfPremiumToCoding
                    },
                );

                if (this.hasAllocationOfPremiumToCoding) {
                    genericData.push(...this.allocationOfPremiumToCodingValues);
                }

                if (this.value.adjustment_premium_period_of_credit) {
                    genericData.push(
                        {
                            text: 'Adjustment Premium Period of Credit',
                            value: this.value.adjustment_premium_period_of_credit
                        }
                    );
                }

                if (this.value.overseas_broker_address) {
                    genericData.push(
                        {
                            text: 'Overseas Broker Address',
                            value: this.value.overseas_broker_address
                        }
                    );
                }
            }

            if (this.productHasProperty('total_contract_value')) {
                this.skipProductProperties.push('total_contract_value');
                genericData.push(
                    {
                        text: this.getPropertyLabel('total_contract_value'),
                        value: this.applyFormatFilter('total_contract_value', 'currency'),
                    },
                );
            }

            genericData.push(
                {
                    text: this.getPropertyLabel('contract_value'),
                    value: this.value.current_contract_value ? this.formatCurrency(this.value.current_contract_value) : this.formatCurrency(this.value.contract_value)
                },
            );

            if (this.productHasProperty('indemnity')) {
                genericData.push({
                    text: this.getPropertyLabel('indemnity'),
                    value: this.$options.filters.percent(this.value.indemnity)
                })
            }

            if (this.productHasProperty('limit_of_liability')) {
                genericData.push({
                    text: this.getPropertyLabel('limit_of_liability'),
                    value: this.formatCurrency(this.value.limit_of_liability)
                })
            }

            return genericData;
        },
        riskPeriodFields() {
            let riskPeriodFields = [];

            this.productQuestions.forEach(question => {
                if (!this.isProductDataProperty(question)) {
                    return false;
                }

                if (question.hasOwnProperty('type') && this.isTypeRiskPeriod(question.type) && this.value.product_data[question.field]) {
                    riskPeriodFields.push({
                        text: this.getPropertyLabel(question.field),
                        value: this.applyFormatFilter(question.field, question.type)
                    });
                }
            });

            return riskPeriodFields;
        },
        datesAndTotalRiskPeriodData() {
            let datesData = [];
            let totalRiskPeriod;

            if (this.isFirmOrder || this.isDeclaration) {
                const risk = this.isFirmOrder ? this.firmOrder : this.value;

                const startDate = this.$moment(risk.inception_date);
                //if off risk then use the off risk date
                const endDate = this.isOffRisk ? this.$moment(risk.off_risk_at) : this.$moment(risk.expiry_date);
                totalRiskPeriod = endDate.diff(startDate, 'days');
                const inceptionDate = risk.inception_date;

                datesData.push(
                    {
                        text: this.getPropertyLabel('start_date'),
                        value: this.$options.filters.formatDate(inceptionDate)
                    },
                    {
                        text: this.getPropertyLabel('end_date'),
                        value: this.$options.filters.formatDate(endDate)
                    }
                );
            } else {
                totalRiskPeriod = this.value.total_risk_period;
            }

            datesData.push(
                {
                    text: this.getPropertyLabel('total_risk_period'),
                    value: this.$options.filters.yearsAndDays(totalRiskPeriod)
                }
            );

            return datesData;
        },
        captiveInsuranceData() {
            let captiveInsuranceData = [];

            if ((!this.isFirmOrder && !this.isDeclaration) || !this.isCaptiveReinsurance) {
                return captiveInsuranceData;
            }

            const risk = this.isFirmOrder ? this.firmOrder : this.value;

            captiveInsuranceData.push(
                {
                    text: 'Booked location',
                    value: risk.booked_location.name
                },
                {
                    text: 'Insured transaction',
                    value: risk.insured_transaction
                },
                {
                    text: 'Special conditions',
                    value: risk.special_conditions
                },
                {
                    text: 'Obligor address',
                    value: risk.obligor_address
                }
            );

            return captiveInsuranceData;
        },
        productDataFields() {
            let productData = [];

            this.productQuestions.forEach(question => {
                if (!this.isProductDataProperty(question) || this.isSkippedProperty(question.field)) {
                    return;
                }

                const productDataField = question;

                if (productDataField.hasOwnProperty('type') && !this.isTypeRiskPeriod(productDataField.type)) {
                    const value = this.applyFormatFilter(productDataField.field, productDataField.type);
                    const suffix = productDataField.hasOwnProperty('suffix') ? productDataField.suffix : '';

                    productData.push({
                        text: this.getPropertyLabel(productDataField.field),
                        value: `${value}${suffix}`
                    });
                }
            });

            return productData;
        },
        placingBrokerContactData() {
            return [
                {
                    text: 'Placing broker contact',
                    value: this.value.placing_broker_contact?.name
                },
            ]
        },
        brokersData() {
            let brokersData = [];

            brokersData.push(
                {
                    text: 'Placing broker',
                    value: this.value.placing_broker_company.name
                }
            );

            if (this.value.producing_broker_company !== null) {
                brokersData.push(
                    {
                        text: 'Producing broker',
                        value: this.value.producing_broker_company.name
                    }
                );
            }

            return brokersData;
        },
        declarationData() {
            let declarationData = [];
            declarationData.push(
                {
                    text: 'Bound date',
                    value: this.$options.filters.formatDate(this.value.created_at)
                },
                {
                    text: 'Updated date',
                    value: this.$options.filters.formatDate(this.value.updated_at)
                },
            );

            if (this.value.final_premium) {
                declarationData.push(
                    {
                        text: 'Final premium',
                        value: this.$options.filters.currency(this.value.final_premium, this.value.currency.code)
                    }
                );
            } else if (this.value.classic_declaration_id) {
                    declarationData.push(
                        {
                            text: 'Tepfin classic estimated premium',
                            value: this.$options.filters.currency(Math.round(this.value.classic_estimated_premium), this.value.currency.code)
                        }
                    );
                }

            if (this.isOffRisk) {
                declarationData.push(
                    {
                        text: 'Off risk date',
                        value: this.$options.filters.formatDate(this.value.off_risk_at)
                    }
                );
            }

            return declarationData;
        },
        hasAllocationOfPremiumToCoding() {
            return this.value.risk_codes && this.value.risk_codes.length > 0;
        },
        allocationOfPremiumToCoding() {
            if (!this.hasAllocationOfPremiumToCoding) {
                return '';
            }

            return this.value.risk_codes.map((submissionRiskCode) => {
                return submissionRiskCode.risk_code.name;
            }).join(', ');
        },
        allocationOfPremiumToCodingValues() {
            if (!this.hasAllocationOfPremiumToCoding) {
                return null;
            }

            return this.value.risk_codes.map((submissionRiskCode) => {
                return {
                    text: submissionRiskCode.risk_code.name.concat(' Allocation'),
                    value: this.$options.filters.percent(submissionRiskCode.value)
                }
            });
        },
        hasApprovedOffRiskEndorsement() {
            return this.value.endorsements?.some(endorsement => endorsement.type === 'off_risk' && endorsement.status === 'approved');
        }
    },
    methods: {
        formatCurrency(value) {
            return this.$options.filters.currency(value, this.value.currency.code);
        },
        applyFormatFilter(key, format) {
            switch (format) {
                case 'currency':
                    return this.formatCurrency(this.value.product_data[key]);
                case 'days':
                case 'risk_period':
                    return this.$options.filters.yearsAndDays(this.value.product_data[key]);
                case 'yes_no':
                case 'boolean':
                    return this.value.product_data[key] ? 'Yes' : 'No';
                default:
                    return this.value.product_data[key] || '';
            }
        },
        isSkippedProperty(property) {
            return this.skipProductProperties.includes(property);
        },
    }
}
</script>
